"use client";

import { useMemo, useState } from "react";
import type { Amenity_Row } from "./types";
import { Amenity_Category_Layer } from "@/lib/parcelq/mappers";
import {
  LonLat_To_Svg_Point,
  Parcel_Wgs84_Centroid,
  Ring_To_Svg_Points,
  Ring_To_Wgs84,
  Tiles_For_Viewport,
  Zoom_For_Bounds,
  type Wgs84_Point,
} from "@/lib/parcelq/map-preview";
import { ParcelQ_Label_Mono_Class } from "./parcelq-styles";

const Map_Width = 640;
const Map_Height = 420;

/** Client-requested rings for affordable-housing radius narrative. */
const Radius_Ring_Miles = [0.25, 0.5, 0.75, 1.0] as const;

const Ring_Label: Record<number, string> = {
  0.25: "1/4 mi",
  0.5: "1/2 mi",
  0.75: "3/4 mi",
  1.0: "1 mi",
};

const Ring_Stroke = ["#b45309", "#d97706", "#f59e0b", "#ca8a04"];

const Layer_Colors: Record<string, string> = {
  Transit: "#0284c7",
  Grocery: "#16a34a",
  Pharmacy: "#7c3aed",
  Medical: "#dc2626",
  Schools: "#ca8a04",
  Parks: "#059669",
  Libraries: "#2563eb",
  Community: "#ea580c",
  "Community services": "#db2777",
  "Shopping & employment": "#a16207",
  "Housing resources": "#c026d3",
  Other: "#64748b",
};

type Amenities_Map_Props = {
  Latitude: number;
  Longitude: number;
  Rings?: number[][][];
  Amenities_Rows: Amenity_Row[];
  Radius_Miles?: number;
};

function Miles_To_Deg_Lat(Miles: number): number {
  return Miles / 69;
}

function Miles_To_Deg_Lon(Miles: number, Lat: number): number {
  const Cos = Math.cos((Lat * Math.PI) / 180);
  return Miles / (69 * Math.max(Math.abs(Cos), 1e-6));
}

function Ellipse_Svg_Points(
  Center: Wgs84_Point,
  Radius_Mi: number,
  Zoom: number,
  Width: number,
  Height: number,
  Steps = 64,
): string {
  const R_Lat = Miles_To_Deg_Lat(Radius_Mi);
  const R_Lon = Miles_To_Deg_Lon(Radius_Mi, Center.lat);
  const Parts: string[] = [];
  for (let I = 0; I <= Steps; I++) {
    const Angle = (I / Steps) * Math.PI * 2;
    const Lon = Center.lon + R_Lon * Math.cos(Angle);
    const Lat = Center.lat + R_Lat * Math.sin(Angle);
    const Point = LonLat_To_Svg_Point(Lon, Lat, Center, Zoom, Width, Height);
    Parts.push(`${Point.x.toFixed(1)},${Point.y.toFixed(1)}`);
  }
  return Parts.join(" ");
}

export function Amenities_Map({
  Latitude,
  Longitude,
  Rings = [],
  Amenities_Rows,
  Radius_Miles = 1.5,
}: Amenities_Map_Props) {
  const Layers_All = useMemo(() => {
    const Set_Layers = new Set<string>();
    for (const Row of Amenities_Rows) {
      Set_Layers.add(Amenity_Category_Layer(Row.Category));
    }
    return [...Set_Layers].sort();
  }, [Amenities_Rows]);

  const [Layers_On, Layers_On_Set] = useState<Record<string, boolean>>({});

  const Layers_Active = useMemo(() => {
    const Active: Record<string, boolean> = {};
    for (const Layer of Layers_All) {
      Active[Layer] = Layers_On[Layer] ?? true;
    }
    return Active;
  }, [Layers_All, Layers_On]);

  const Display_Rings = useMemo(() => {
    return Radius_Ring_Miles.filter((Miles) => Miles <= Radius_Miles + 1e-9);
  }, [Radius_Miles]);

  const Ring_Wgs84 = Ring_To_Wgs84(Rings);
  const Centroid = Parcel_Wgs84_Centroid(Latitude, Longitude, Ring_Wgs84);
  const Pins = useMemo(() => {
    return Amenities_Rows.flatMap((Row) => {
      const Layer = Amenity_Category_Layer(Row.Category);
      if (!Layers_Active[Layer]) return [];
      const Lat = Row.Match?.lat;
      const Lon = Row.Match?.lon;
      if (typeof Lat !== "number" || typeof Lon !== "number") return [];
      if (!Number.isFinite(Lat) || !Number.isFinite(Lon)) return [];
      return [{ Row, Layer, Lat, Lon }];
    });
  }, [Amenities_Rows, Layers_Active]);

  const Bounds = useMemo(() => {
    const Points: Wgs84_Point[] = [{ lat: Centroid.lat, lon: Centroid.lon }];
    for (const Pin of Pins) {
      Points.push({ lat: Pin.Lat, lon: Pin.Lon });
    }
    for (const [Lon, Lat] of Ring_Wgs84) {
      Points.push({ lat: Lat, lon: Lon });
    }
    const Outer = Display_Rings.length ? Display_Rings[Display_Rings.length - 1] : 1;
    const Pad_Lat = Miles_To_Deg_Lat(Outer * 1.08);
    const Pad_Lon = Miles_To_Deg_Lon(Outer * 1.08, Centroid.lat);
    Points.push({ lat: Centroid.lat + Pad_Lat, lon: Centroid.lon + Pad_Lon });
    Points.push({ lat: Centroid.lat - Pad_Lat, lon: Centroid.lon - Pad_Lon });
    const Lats = Points.map((P) => P.lat);
    const Lons = Points.map((P) => P.lon);
    return {
      minLat: Math.min(...Lats),
      maxLat: Math.max(...Lats),
      minLon: Math.min(...Lons),
      maxLon: Math.max(...Lons),
    };
  }, [Centroid, Pins, Ring_Wgs84, Display_Rings]);

  const Zoom = Zoom_For_Bounds(Bounds, Map_Width, Map_Height);
  const Tiles = Tiles_For_Viewport(Centroid, Zoom, Map_Width, Map_Height);
  const Polygon_Points = Ring_To_Svg_Points(Ring_Wgs84, Centroid, Zoom, Map_Width, Map_Height);
  const Parcel_Pin = LonLat_To_Svg_Point(
    Centroid.lon,
    Centroid.lat,
    Centroid,
    Zoom,
    Map_Width,
    Map_Height,
  );

  function Layer_Toggle(Layer: string) {
    Layers_On_Set((Prev) => ({
      ...Prev,
      [Layer]: !(Prev[Layer] ?? true),
    }));
  }

  if (!Number.isFinite(Latitude) || !Number.isFinite(Longitude)) {
    return (
      <p className="text-sm text-pq-fg-muted">Map unavailable — parcel coordinates missing.</p>
    );
  }

  return (
    <div className="rounded-2xl border border-amber-900/15 bg-pq-bg-elevated p-5 shadow-sm">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <p className={ParcelQ_Label_Mono_Class}>Interactive amenity map</p>
          <p className="mt-1 text-sm text-pq-fg-muted">
            Labeled rings at 1/4, 1/2, 3/4, and 1 mile. Toggle amenity layers.
          </p>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {Layers_All.map((Layer) => {
          const On = Layers_Active[Layer];
          return (
            <button
              key={Layer}
              type="button"
              onClick={() => Layer_Toggle(Layer)}
              className={`rounded-md border px-2.5 py-1 text-xs font-medium transition ${
                On
                  ? "border-amber-700/40 bg-pq-bg-soft text-amber-950"
                  : "border-pq-border bg-transparent text-pq-fg-subtle"
              }`}
              style={On ? { boxShadow: `inset 0 -2px 0 ${Layer_Colors[Layer] || "#64748b"}` } : undefined}
            >
              {Layer}
            </button>
          );
        })}
      </div>

      <div
        className="relative mx-auto w-full overflow-hidden rounded-lg border border-pq-border bg-slate-100"
        style={{ maxWidth: Map_Width, aspectRatio: `${Map_Width} / ${Map_Height}` }}
        role="img"
        aria-label="Amenity map with mile radius rings and toggleable layers"
      >
        <div className="relative h-full w-full" style={{ minHeight: Map_Height }}>
          {Tiles.map((Tile) => (
            <img
              key={`${Zoom}-${Tile.x}-${Tile.y}`}
              src={`https://tile.openstreetmap.org/${Zoom}/${Tile.x}/${Tile.y}.png`}
              alt=""
              draggable={false}
              className="pointer-events-none absolute max-w-none"
              style={{ left: Tile.left, top: Tile.top, width: 256, height: 256 }}
            />
          ))}
          <svg
            viewBox={`0 0 ${Map_Width} ${Map_Height}`}
            className="pointer-events-none absolute inset-0 h-full w-full"
            aria-hidden
          >
            {Display_Rings.map((Miles, Index) => {
              const Points = Ellipse_Svg_Points(Centroid, Miles, Zoom, Map_Width, Map_Height);
              const Color = Ring_Stroke[Index] || "#b45309";
              const Edge = LonLat_To_Svg_Point(
                Centroid.lon + Miles_To_Deg_Lon(Miles, Centroid.lat),
                Centroid.lat,
                Centroid,
                Zoom,
                Map_Width,
                Map_Height,
              );
              return (
                <g key={Miles}>
                  <polyline
                    points={Points}
                    fill="none"
                    stroke={Color}
                    strokeWidth="2"
                    strokeDasharray="6 4"
                    opacity="0.9"
                  />
                  <text
                    x={Edge.x - 4}
                    y={Edge.y - 4}
                    fill={Color}
                    fontSize="11"
                    fontWeight="700"
                    textAnchor="end"
                  >
                    {Ring_Label[Miles] || `${Miles} mi`}
                  </text>
                </g>
              );
            })}
            {Polygon_Points ? (
              <polygon
                points={Polygon_Points}
                fill="rgba(180,83,9,0.18)"
                stroke="#b45309"
                strokeWidth="2"
              />
            ) : null}
            <circle cx={Parcel_Pin.x} cy={Parcel_Pin.y} r="6" fill="#b45309" stroke="#fff7ed" strokeWidth="1.5" />
            {Pins.map((Pin) => {
              const Point = LonLat_To_Svg_Point(Pin.Lon, Pin.Lat, Centroid, Zoom, Map_Width, Map_Height);
              const Color = Layer_Colors[Pin.Layer] || "#64748b";
              return (
                <circle
                  key={Pin.Row.Id}
                  cx={Point.x}
                  cy={Point.y}
                  r="5"
                  fill={Color}
                  stroke="#ffffff"
                  strokeWidth="1"
                >
                  <title>{`${Pin.Row.Name} (${Pin.Layer})`}</title>
                </circle>
              );
            })}
          </svg>
        </div>
      </div>
      <p className="mt-3 text-xs text-pq-fg-subtle">
        Showing {Pins.length} amenity pin(s). Parcel pin in amber. Dashed rings mark 1/4–1 mile.
      </p>
    </div>
  );
}
